import { ethers } from "ethers";
import { PinnedHistory, type HistoryCheckpoint } from "./pinnedHistory";

export const FLAT_JOINSPLIT_V2_MERKLE_DEPTH = 24;
export const FLAT_JOINSPLIT_V2_MAX_LEAVES = 2 ** FLAT_JOINSPLIT_V2_MERKLE_DEPTH;

const FIELD_MODULUS = BigInt(
  "21888242871839275222246405745257275088548364400416034343698204186575808495617",
);
const LEAF_EVENT =
  "event LeafInsertedInTree(uint256 indexed treeIndex, bytes32 leaf, uint256 leafIndex)";
const LEAF_INTERFACE = new ethers.Interface([LEAF_EVENT]);
const LEAF_EVENT_FRAGMENT = LEAF_INTERFACE.getEvent("LeafInsertedInTree");
export const FLAT_JOINSPLIT_V2_LEAF_EVENT_TOPIC = LEAF_EVENT_FRAGMENT.topicHash;

export interface FlatJoinSplitV2RawLog {
  address: string;
  topics: readonly string[];
  data: string;
  blockNumber: number;
  blockHash: string;
  transactionIndex: number;
  index?: number;
  logIndex?: number;
  transactionHash?: string;
  removed?: boolean;
}

export interface FlatJoinSplitV2LogFilter {
  address: string;
  topics: readonly (string | readonly string[] | null)[];
  fromBlock: number;
  toBlock: number;
}

export interface FlatJoinSplitV2Block {
  number: number;
  hash: string | null;
}

/** Minimal read-only provider surface used by the on-chain indexer. */
export interface FlatJoinSplitV2LogProvider {
  getBlock(blockTag: number): Promise<FlatJoinSplitV2Block | null>;
  getLogs(
    filter: FlatJoinSplitV2LogFilter,
  ): Promise<readonly FlatJoinSplitV2RawLog[]>;
}

export interface FlatJoinSplitV2LeafInsertion {
  treeIndex: number;
  leafIndex: number;
  commitment: string;
  blockNumber: number;
  blockHash: string;
  transactionIndex: number;
  logIndex: number;
  transactionHash?: string;
}

export interface FlatJoinSplitV2MerklePath {
  treeIndex: number;
  leafIndex: number;
  leaf: string;
  root: string;
  pathElements: string[];
  pathIndices: boolean[];
}

export interface FlatJoinSplitV2CommitmentLocation {
  treeIndex: number;
  leafIndex: number;
}

export interface FlatJoinSplitV2TreeScanRequest {
  provider: FlatJoinSplitV2LogProvider;
  pool: string;
  treeIndex: number;
  fromBlock: number;
  toBlock: number;
  /** Defaults to 5,000 and is capped at 10,000. */
  chunkSize?: number;
  /** Optional independently supplied pin. The provider must agree exactly. */
  expectedToBlockHash?: string;
  /** Existing canonical leaves when continuing from a persisted cursor. */
  existingLeaves?: readonly string[];
  /** Optional on-chain cross-check at the pinned block. */
  expectedRoot?: string;
  /** Optional on-chain cross-check at the pinned block. */
  expectedNextLeafIndex?: number;
}

export interface FlatJoinSplitV2TreeScanResult {
  pool: string;
  treeIndex: number;
  fromBlock: number;
  toBlock: number;
  toBlockHash: string;
  leaves: string[];
  insertions: FlatJoinSplitV2LeafInsertion[];
  root: string;
  nextLeafIndex: number;
  tree: FlatJoinSplitV2MerkleTree;
}

interface PoseidonLike {
  (inputs: bigint[]): unknown;
  F: {
    toObject(value: unknown): bigint | number | string;
  };
}

let poseidonPromise: Promise<PoseidonLike> | null = null;

async function getPoseidon(): Promise<PoseidonLike> {
  if (!poseidonPromise) {
    poseidonPromise = import("circomlibjs").then(async (module) =>
      module.buildPoseidon(),
    ) as Promise<PoseidonLike>;
  }
  return poseidonPromise;
}

function fail(message: string): never {
  throw new Error(`Flat JoinSplit V2 Merkle index: ${message}`);
}

function requireSafeInteger(
  value: unknown,
  label: string,
  maximum = Number.MAX_SAFE_INTEGER,
): number {
  if (
    typeof value !== "number" ||
    !Number.isSafeInteger(value) ||
    value < 0 ||
    value > maximum
  ) {
    fail(`${label} must be a non-negative safe integer`);
  }
  return value;
}

function requireAddress(value: unknown, label: string): string {
  if (typeof value !== "string" || !ethers.isAddress(value)) {
    fail(`${label} must be a valid address`);
  }
  return ethers.getAddress(value);
}

function requireBytes32(value: unknown, label: string): string {
  if (typeof value !== "string" || !ethers.isHexString(value, 32)) {
    fail(`${label} must be exactly 32 bytes`);
  }
  return value.toLowerCase();
}

function requireCanonicalField(
  value: unknown,
  label: string,
  nonzero = false,
): bigint {
  const encoded = requireBytes32(value, label);
  const scalar = BigInt(encoded);
  if (scalar >= FIELD_MODULUS || (nonzero && scalar === 0n)) {
    fail(`${label} is not a canonical field element`);
  }
  return scalar;
}

function fieldHex(value: bigint): string {
  if (value < 0n || value >= FIELD_MODULUS) {
    fail("internal hash is not a canonical field element");
  }
  return ethers.toBeHex(value, 32).toLowerCase();
}

function logPosition(log: FlatJoinSplitV2RawLog): [number, number, number] {
  const rawLogIndex = log.index ?? log.logIndex;
  if (rawLogIndex === undefined) fail("log index is missing");
  return [
    requireSafeInteger(log.blockNumber, "log blockNumber"),
    requireSafeInteger(log.transactionIndex, "log transactionIndex"),
    requireSafeInteger(rawLogIndex, "log index"),
  ];
}

function compareLogs(
  left: FlatJoinSplitV2RawLog,
  right: FlatJoinSplitV2RawLog,
): number {
  const a = logPosition(left);
  const b = logPosition(right);
  return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
}

function parseLeafInsertion(
  log: FlatJoinSplitV2RawLog,
  pool: string,
  expectedTreeIndex: number,
  fromBlock: number,
  toBlock: number,
): FlatJoinSplitV2LeafInsertion {
  if (log.removed === true) fail("provider returned a removed log");
  if (requireAddress(log.address, "log address") !== pool) {
    fail("provider returned a leaf log for the wrong pool");
  }
  const blockNumber = requireSafeInteger(log.blockNumber, "log blockNumber");
  if (blockNumber < fromBlock || blockNumber > toBlock) {
    fail("provider returned a leaf log outside the requested block range");
  }
  if (!Array.isArray(log.topics) || log.topics.length !== 2) {
    fail("leaf log has an invalid topic count");
  }
  if (
    requireBytes32(log.topics[0], "leaf event topic") !==
    FLAT_JOINSPLIT_V2_LEAF_EVENT_TOPIC.toLowerCase()
  ) {
    fail("provider returned a log with the wrong event signature");
  }

  let parsed: ethers.LogDescription | null;
  try {
    parsed = LEAF_INTERFACE.parseLog({
      topics: [...log.topics],
      data: log.data,
    });
  } catch {
    return fail("leaf log ABI decoding failed");
  }
  if (!parsed || parsed.name !== "LeafInsertedInTree") {
    fail("leaf log ABI decoding returned the wrong event");
  }

  const rawTreeIndex = BigInt(parsed.args.treeIndex);
  const rawLeafIndex = BigInt(parsed.args.leafIndex);
  if (rawTreeIndex > BigInt(Number.MAX_SAFE_INTEGER)) {
    fail("leaf log tree index exceeds the safe integer range");
  }
  if (rawLeafIndex >= BigInt(FLAT_JOINSPLIT_V2_MAX_LEAVES)) {
    fail("leaf log index exceeds the depth-24 tree capacity");
  }
  const treeIndex = Number(rawTreeIndex);
  const leafIndex = Number(rawLeafIndex);
  if (treeIndex !== expectedTreeIndex) {
    fail("provider returned a leaf log for the wrong tree");
  }
  const commitment = fieldHex(
    requireCanonicalField(parsed.args.leaf, "leaf commitment", true),
  );
  const rawLogIndex = log.index ?? log.logIndex;
  if (rawLogIndex === undefined) fail("log index is missing");

  return {
    treeIndex,
    leafIndex,
    commitment,
    blockNumber,
    blockHash: requireBytes32(log.blockHash, "log block hash"),
    transactionIndex: requireSafeInteger(
      log.transactionIndex,
      "log transactionIndex",
    ),
    logIndex: requireSafeInteger(rawLogIndex, "log index"),
    transactionHash: log.transactionHash
      ? requireBytes32(log.transactionHash, "log transaction hash")
      : undefined,
  };
}

async function poseidon2(
  poseidon: PoseidonLike,
  left: bigint,
  right: bigint,
): Promise<bigint> {
  const result = poseidon([left, right]);
  return BigInt(poseidon.F.toObject(result).toString());
}

/**
 * Sparse, append-only depth-24 Poseidon tree matching ShieldedPoolJoinSplit.
 * It stores public nodes only; no note secrets are accepted or retained.
 */
export class FlatJoinSplitV2MerkleTree {
  readonly treeIndex: number;
  private readonly poseidon: PoseidonLike;
  private readonly zeros: bigint[];
  private readonly nodes: Array<Map<number, bigint>>;
  private readonly leaves: bigint[];
  private readonly locations: Map<string, number>;

  private constructor(
    treeIndex: number,
    poseidon: PoseidonLike,
    zeros: bigint[],
  ) {
    this.treeIndex = requireSafeInteger(treeIndex, "treeIndex");
    this.poseidon = poseidon;
    this.zeros = zeros;
    this.nodes = Array.from(
      { length: FLAT_JOINSPLIT_V2_MERKLE_DEPTH + 1 },
      () => new Map<number, bigint>(),
    );
    this.leaves = [];
    this.locations = new Map<string, number>();
  }

  static async create(
    treeIndex: number,
    existingLeaves: readonly string[] = [],
  ): Promise<FlatJoinSplitV2MerkleTree> {
    const poseidon = await getPoseidon();
    const zeros = [0n];
    for (let level = 0; level < FLAT_JOINSPLIT_V2_MERKLE_DEPTH; level += 1) {
      zeros.push(await poseidon2(poseidon, zeros[level], zeros[level]));
    }
    const tree = new FlatJoinSplitV2MerkleTree(treeIndex, poseidon, zeros);
    for (const leaf of existingLeaves) {
      await tree.append(leaf);
    }
    return tree;
  }

  /** Copy public nodes without rehashing every historical leaf. The old
   * checkpoint stays immutable if the new scan fails or is reorganized. */
  clone(): FlatJoinSplitV2MerkleTree {
    const tree = new FlatJoinSplitV2MerkleTree(
      this.treeIndex,
      this.poseidon,
      this.zeros,
    );
    this.nodes.forEach((nodes, level) =>
      nodes.forEach((value, index) => tree.nodes[level].set(index, value)),
    );
    for (const leaf of this.leaves) tree.leaves.push(leaf);
    this.locations.forEach((value, key) => tree.locations.set(key, value));
    return tree;
  }

  get nextLeafIndex(): number {
    return this.leaves.length;
  }

  get root(): string {
    return fieldHex(
      this.nodes[FLAT_JOINSPLIT_V2_MERKLE_DEPTH].get(0) ??
        this.zeros[FLAT_JOINSPLIT_V2_MERKLE_DEPTH],
    );
  }

  getLeaves(): string[] {
    return this.leaves.map(fieldHex);
  }

  locate(commitment: string): FlatJoinSplitV2CommitmentLocation | null {
    const key = fieldHex(
      requireCanonicalField(commitment, "commitment lookup", true),
    );
    const leafIndex = this.locations.get(key);
    return leafIndex === undefined
      ? null
      : { treeIndex: this.treeIndex, leafIndex };
  }

  async append(commitment: string): Promise<number> {
    if (this.leaves.length >= FLAT_JOINSPLIT_V2_MAX_LEAVES) {
      fail("tree is full");
    }
    const leaf = requireCanonicalField(commitment, "commitment", true);
    const key = fieldHex(leaf);
    if (this.locations.has(key)) {
      fail("duplicate commitment");
    }

    const leafIndex = this.leaves.length;
    this.leaves.push(leaf);
    this.locations.set(key, leafIndex);
    this.nodes[0].set(leafIndex, leaf);

    let index = leafIndex;
    for (let level = 0; level < FLAT_JOINSPLIT_V2_MERKLE_DEPTH; level += 1) {
      const leftIndex = index & ~1;
      const left = this.nodes[level].get(leftIndex) ?? this.zeros[level];
      const right = this.nodes[level].get(leftIndex + 1) ?? this.zeros[level];
      const parent = await poseidon2(this.poseidon, left, right);
      index >>= 1;
      this.nodes[level + 1].set(index, parent);
    }
    return leafIndex;
  }

  path(leafIndex: number): FlatJoinSplitV2MerklePath {
    const index = requireSafeInteger(
      leafIndex,
      "leafIndex",
      FLAT_JOINSPLIT_V2_MAX_LEAVES - 1,
    );
    if (index >= this.leaves.length) fail("leafIndex is not populated");

    const pathElements: string[] = [];
    const pathIndices: boolean[] = [];
    let currentIndex = index;
    for (let level = 0; level < FLAT_JOINSPLIT_V2_MERKLE_DEPTH; level += 1) {
      const siblingIndex = currentIndex ^ 1;
      pathElements.push(
        fieldHex(this.nodes[level].get(siblingIndex) ?? this.zeros[level]),
      );
      pathIndices.push((currentIndex & 1) === 1);
      currentIndex >>= 1;
    }

    return {
      treeIndex: this.treeIndex,
      leafIndex: index,
      leaf: fieldHex(this.leaves[index]),
      root: this.root,
      pathElements,
      pathIndices,
    };
  }
}

async function requirePinnedBlock(
  provider: FlatJoinSplitV2LogProvider,
  toBlock: number,
  label: string,
): Promise<string> {
  const block = await provider.getBlock(toBlock);
  if (!block || block.number !== toBlock || block.hash === null) {
    fail(`provider could not resolve ${label}`);
  }
  return requireBytes32(block.hash, label);
}

/**
 * Scans one tree transactionally. No caller-visible tree is returned until
 * all logs, the depth-24 root, and the pinned block hash have been checked.
 */
export async function scanFlatJoinSplitV2Tree(
  request: FlatJoinSplitV2TreeScanRequest,
): Promise<FlatJoinSplitV2TreeScanResult> {
  const { toBlock } = validateTreeScanRequest(request);
  const hash = await requirePinnedBlock(
    request.provider,
    toBlock,
    "pinned toBlock hash",
  );
  const result = await buildTreeWithinPin(request, hash);
  const finalHash = await requirePinnedBlock(
    request.provider,
    toBlock,
    "rechecked toBlock hash",
  );
  if (finalHash !== hash)
    fail("pinned toBlock changed while logs were scanned");
  return result;
}

function validateTreeScanRequest(request: FlatJoinSplitV2TreeScanRequest) {
  if (
    !request.provider ||
    typeof request.provider.getBlock !== "function" ||
    typeof request.provider.getLogs !== "function"
  ) {
    fail("provider must support getBlock and getLogs");
  }
  const pool = requireAddress(request.pool, "pool");
  const treeIndex = requireSafeInteger(request.treeIndex, "treeIndex");
  const fromBlock = requireSafeInteger(request.fromBlock, "fromBlock");
  const toBlock = requireSafeInteger(request.toBlock, "toBlock");
  if (fromBlock > toBlock) fail("fromBlock cannot exceed toBlock");
  const chunkSize = request.chunkSize ?? 5_000;
  if (!Number.isSafeInteger(chunkSize) || chunkSize < 1 || chunkSize > 10_000) {
    fail("chunkSize must be an integer in 1..10000");
  }

  return { pool, treeIndex, fromBlock, toBlock, chunkSize };
}

// Private builder: both callers surround it with fresh canonical hash reads.
// Keeping it private prevents a caller from accidentally bypassing that guard.
async function buildTreeWithinPin(
  request: FlatJoinSplitV2TreeScanRequest,
  initialBlockHash: string,
  prefix?: FlatJoinSplitV2MerkleTree,
): Promise<FlatJoinSplitV2TreeScanResult> {
  const { pool, treeIndex, fromBlock, toBlock, chunkSize } =
    validateTreeScanRequest(request);
  if (
    request.expectedToBlockHash !== undefined &&
    requireBytes32(request.expectedToBlockHash, "expected toBlock hash") !==
      initialBlockHash
  ) {
    fail("provider toBlock hash does not match the supplied pin");
  }

  const treeTopic = ethers.toBeHex(BigInt(treeIndex), 32).toLowerCase();
  const rawLogs: FlatJoinSplitV2RawLog[] = [];
  // The ranges are independent reads of one pinned history. Use the existing
  // app RPC queue's four-request ceiling without increasing the number of
  // requests or its rate budget. Never return partial history after a failure.
  let nextBlock = fromBlock;
  let failed = false;
  let failure: unknown;
  const readRanges = async () => {
    while (!failed && nextBlock <= toBlock) {
      const chunkFrom = nextBlock;
      const chunkTo = Math.min(toBlock, chunkFrom + chunkSize - 1);
      nextBlock = chunkTo + 1;
      try {
        const logs = await request.provider.getLogs({
          address: pool,
          topics: [FLAT_JOINSPLIT_V2_LEAF_EVENT_TOPIC, treeTopic],
          fromBlock: chunkFrom,
          toBlock: chunkTo,
        });
        if (!Array.isArray(logs))
          fail("provider returned a non-array log result");
        rawLogs.push(...logs);
      } catch (error) {
        if (!failed) failure = error;
        failed = true;
      }
    }
  };
  await Promise.all(Array.from({ length: 4 }, readRanges));
  if (failed) throw failure;

  rawLogs.sort(compareLogs);
  const insertions = rawLogs.map((log) =>
    parseLeafInsertion(log, pool, treeIndex, fromBlock, toBlock),
  );
  for (const insertion of insertions) {
    if (
      insertion.blockNumber === toBlock &&
      insertion.blockHash !== initialBlockHash
    ) {
      fail("a toBlock log does not belong to the pinned block hash");
    }
  }
  const tree =
    prefix?.clone() ??
    (await FlatJoinSplitV2MerkleTree.create(
      treeIndex,
      request.existingLeaves ?? [],
    ));
  let expectedIndex = tree.nextLeafIndex;
  for (const insertion of insertions) {
    if (insertion.leafIndex !== expectedIndex) {
      fail(
        `non-contiguous leaf index: expected ${expectedIndex}, received ${insertion.leafIndex}`,
      );
    }
    await tree.append(insertion.commitment);
    expectedIndex += 1;
  }

  if (
    request.expectedNextLeafIndex !== undefined &&
    requireSafeInteger(
      request.expectedNextLeafIndex,
      "expectedNextLeafIndex",
      FLAT_JOINSPLIT_V2_MAX_LEAVES,
    ) !== tree.nextLeafIndex
  ) {
    fail("computed next leaf index does not match the supplied on-chain value");
  }
  if (
    request.expectedRoot !== undefined &&
    fieldHex(requireCanonicalField(request.expectedRoot, "expected root")) !==
      tree.root
  ) {
    fail("computed Merkle root does not match the supplied on-chain root");
  }

  return {
    pool,
    treeIndex,
    fromBlock,
    toBlock,
    toBlockHash: initialBlockHash,
    leaves: tree.getLeaves(),
    insertions,
    root: tree.root,
    nextLeafIndex: tree.nextLeafIndex,
    tree,
  };
}

const treeHistory = new PinnedHistory<FlatJoinSplitV2TreeScanResult>(
  (value) => value.leaves.length <= 100_000,
);
// Disk checkpoints contain inclusion evidence only, not transaction/log
// metadata. Keep them out of journal reconciliation's event-history cache.
const balanceHistory = new PinnedHistory<FlatJoinSplitV2TreeScanResult>(
  (value) => value.leaves.length <= 100_000,
);

/** Keep public insertion evidence for incremental reads. PinnedHistory owns
 * the before/after checks for both the prefix and the new safe block, so the
 * scanner need not repeat an identical nested pair of canonical reads. */
export async function readFlatJoinSplitV2TreeHistory(
  request: Omit<FlatJoinSplitV2TreeScanRequest, "existingLeaves">,
  persistence?: {
    key: string;
    load: () => Promise<
      HistoryCheckpoint<FlatJoinSplitV2TreeScanResult> | undefined
    >;
    save: (
      checkpoint: HistoryCheckpoint<FlatJoinSplitV2TreeScanResult>,
    ) => Promise<void>;
  },
): Promise<FlatJoinSplitV2TreeScanResult> {
  const { pool } = validateTreeScanRequest(request);
  return (persistence ? balanceHistory : treeHistory).read({
    provider: request.provider,
    key: `${persistence?.key ?? ""}:${pool}:${request.treeIndex}:${request.fromBlock}`,
    load: persistence?.load,
    save: persistence?.save,
    block: request.toBlock,
    hash: request.expectedToBlockHash,
    build: async (previous, pinnedHash) => {
      const result = await buildTreeWithinPin(
        {
          ...request,
          fromBlock: previous ? previous.block + 1 : request.fromBlock,
          existingLeaves: previous?.value.leaves,
        },
        pinnedHash,
        previous?.value.tree,
      );
      return {
        ...result,
        fromBlock: request.fromBlock,
        insertions: [
          ...(previous?.value.insertions || []),
          ...result.insertions,
        ],
      };
    },
    validate: (value) => {
      if (
        (request.expectedRoot !== undefined &&
          value.root.toLowerCase() !== request.expectedRoot.toLowerCase()) ||
        (request.expectedNextLeafIndex !== undefined &&
          value.nextLeafIndex !== request.expectedNextLeafIndex)
      )
        throw new Error(
          "Public tree history does not match the on-chain root or count",
        );
    },
  });
}

/** Test/integration helper for encoding the exact fresh-pool leaf event. */
export function encodeFlatJoinSplitV2LeafEvent(
  treeIndex: number,
  commitment: string,
  leafIndex: number,
): { topics: string[]; data: string } {
  requireSafeInteger(treeIndex, "treeIndex");
  requireSafeInteger(leafIndex, "leafIndex", FLAT_JOINSPLIT_V2_MAX_LEAVES - 1);
  const leaf = fieldHex(requireCanonicalField(commitment, "commitment", true));
  return LEAF_INTERFACE.encodeEventLog(LEAF_EVENT_FRAGMENT, [
    treeIndex,
    leaf,
    leafIndex,
  ]);
}
